import React from 'react';

import { getProgramSurveyResults } from '../../../../api/v2/program_insights';

import useProgramInsightsMetric from '../use_program_insights_metric';
import MetricCard from '../metric_card';
import KpiCards from '../kpi_cards';

import styles from './index.module.css';

const DESCRIPTION =
  'Favorable-response rates for the four value-of-mentorship survey questions, across total program ' +
  'responses and by pre-/post-consent stage. Favorable means Strongly Agree + Agree (or Yes for ' +
  'preparedness).';

const SERIES = [
  { key: 'total', label: 'Total Program', tone: 'total', valueKey: 'total' },
  { key: 'pre', label: 'Pre-consent', tone: 'pre', valueKey: 'pre_consent' },
  { key: 'post', label: 'Post-consent', tone: 'post', valueKey: 'post_consent' },
];

const formatCount = (count) => {
  if (count == null) return null;
  return `${count.toLocaleString()} ${count === 1 ? 'answer' : 'answers'}`;
};

function QuestionRates({ question, split }) {
  const series = SERIES.filter((item) => item.key === 'total' || split);

  return (
    <section className={styles.question}>
      <h3 className={styles.questionLabel}>{question.label}</h3>
      <ul className={styles.series}>
        {series.map((item) => {
          const value = question[item.valueKey];
          const countLabel = formatCount(question.answers?.[item.valueKey]);

          return (
            <li key={item.key} className={styles.row}>
              <span className={styles.seriesName}>
                <span className={`${styles.swatch} ${styles[item.tone]}`} aria-hidden='true' />
                {item.label}
              </span>
              <div className={styles.track}>
                {value != null && value > 0 && (
                  <div className={`${styles.fill} ${styles[item.tone]}`} style={{ width: `${value}%` }} />
                )}
              </div>
              <span className={styles.rate}>{value == null ? '—' : `${value}%`}</span>
              <span className={styles.count}>{countLabel}</span>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

const buildKpis = (data) => {
  const favorable = (value) => (value == null ? '—' : `${value}%`);
  const cards = [{ label: 'Overall Favorable', value: favorable(data.overall.total) }];

  if (data.recruitment_and_retention) {
    cards.push({ label: 'Pre-consent', value: favorable(data.overall.pre_consent) });
    cards.push({ label: 'Post-consent', value: favorable(data.overall.post_consent) });
  }

  cards.push({ label: 'Total Responses', value: data.totals.total_responses.toLocaleString() });
  cards.push({ label: 'Response Rate', value: `${data.totals.response_rate}%` });

  return cards;
};

export default function ProgramSurveyResults({ filters }) {
  const { data, status } = useProgramInsightsMetric(getProgramSurveyResults, filters);

  const hasData = data && data.totals.total_responses > 0;

  return (
    <MetricCard
      title='Program Survey Results'
      subtitle='Survey submitted before or after study consent. One participant can be in both.'
      description={DESCRIPTION}
    >
      {status === 'loading' && <p className={styles.state}>Loading…</p>}
      {status === 'error' && <p className={styles.stateError}>We could not load this metric. Try again shortly.</p>}

      {status === 'loaded' && !hasData && (
        <p className={styles.state}>No survey responses match the current filters yet.</p>
      )}

      {status === 'loaded' && hasData && (
        <>
          <KpiCards cards={buildKpis(data)} />

          <div className={styles.questions}>
            {data.questions.map((question) => (
              <QuestionRates key={question.key} question={question} split={data.recruitment_and_retention} />
            ))}
          </div>
        </>
      )}
    </MetricCard>
  );
}
